import { z } from 'zod';

// Run status enum
export const RunStatusSchema = z.enum(['running', 'succeeded', 'failed', 'cancelled']);
export type RunStatus = z.infer<typeof RunStatusSchema>;

// Execution status enum
export const ExecutionStatusSchema = z.enum(['running', 'succeeded', 'failed', 'cancelled']);
export type ExecutionStatus = z.infer<typeof ExecutionStatusSchema>;

// Node execution status
// 'cancelled' is distinct from 'failed' and 'skipped': the action was reached
// and then intentionally stopped (e.g. by a Terminate action in another branch
// of an If/Switch). It should render amber (warning), not red (error) and not
// gray (never reached).
export const NodeExecutionStatusSchema = z.enum([
  'pending',
  'running',
  'succeeded',
  'failed',
  'skipped',
  'cancelled',
]);
export type NodeExecutionStatus = z.infer<typeof NodeExecutionStatusSchema>;

// Log entry
export const LogEntrySchema = z.object({
  timestamp: z.string(),
  level: z.enum(['info', 'warning', 'error']),
  message: z.string(),
});
export type LogEntry = z.infer<typeof LogEntrySchema>;

// Tool execution within an agent iteration
export const ToolExecutionSchema = z.object({
  toolId: z.string(),
  toolName: z.string(),
  /** The inner action name (e.g., "Send_an_email") - displayed as subtitle */
  actionName: z.string().optional(),
  status: NodeExecutionStatusSchema,
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  duration: z.number().optional(),
  input: z.unknown().optional(),
  output: z.unknown().optional(),
});
export type ToolExecution = z.infer<typeof ToolExecutionSchema>;

// Agent iteration data (one LLM call cycle)
export const AgentIterationSchema = z.object({
  index: z.number(),
  status: NodeExecutionStatusSchema,
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  duration: z.number().optional(),
  input: z.unknown().optional(),
  output: z.unknown().optional(),
  toolExecutions: z.array(ToolExecutionSchema).optional(),
});
export type AgentIteration = z.infer<typeof AgentIterationSchema>;

// Error details for failed actions
export const ErrorDetailsSchema = z.object({
  code: z.string(),
  message: z.string(),
});
export type ErrorDetails = z.infer<typeof ErrorDetailsSchema>;

// A single retry attempt for an action.
// Mirrors the runtime contract FlowOperationRetryHistory: each entry is one
// retried attempt, typically a failed attempt. The final successful attempt is
// generally not represented, and `error` may be omitted even for a failed
// attempt, so consumers must not infer success from its absence. `durationMs` is
// derived (endTime - startTime).
export const RetryAttemptSchema = z.object({
  startTime: z.string(),
  endTime: z.string().optional(),
  /** Derived from startTime/endTime in milliseconds. */
  durationMs: z.number().optional(),
  /** Operation status code for the attempt (e.g. "ServiceUnavailable", "OK"). */
  code: z.string().optional(),
  /** Correlation id the client sent for this attempt. */
  clientRequestId: z.string().optional(),
  /** Correlation id the service returned for this attempt. */
  serviceRequestId: z.string().optional(),
  /**
   * Error for the attempt, when the runtime captured one. May be absent even for
   * a failed attempt, so do not infer success from its absence.
   */
  error: ErrorDetailsSchema.optional(),
});
export type RetryAttempt = z.infer<typeof RetryAttemptSchema>;

// Node execution data
export const NodeExecutionSchema = z.object({
  nodeId: z.string(),
  nodeName: z.string(),
  nodeType: z.string(),
  status: NodeExecutionStatusSchema,
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  duration: z.number().optional(),
  input: z.unknown().optional(),
  output: z.unknown().optional(),
  // Whether the run reported an inputsLink / outputsLink for this node. Used
  // by the action detail panel to decide whether to render the Input / Output
  // tab at all — cancelled or skipped actions often have no link, in which
  // case showing an empty tab is misleading.
  hasInput: z.boolean().optional(),
  hasOutput: z.boolean().optional(),
  logs: z.array(LogEntrySchema).optional(),
  // Error information for failed actions
  error: ErrorDetailsSchema.optional(),
  // Retry attempts the engine made for this action before reaching its final
  // status. Only present when the action retried at least once.
  retryHistory: z.array(RetryAttemptSchema).optional(),
  // Hierarchy support for agents
  iterationCount: z.number().optional(),
  iterations: z.array(AgentIterationSchema).optional(),
  // For tools: reference to parent agent
  parentNodeId: z.string().optional(),
  // Agent metadata for FoundryAgentService actions (modelType, agentId, threadId)
  agentMetadata: z.object({
    modelType: z.string().optional(),
    agentId: z.string().optional(),
    threadId: z.string().optional(),
  }).optional(),
});
export type NodeExecution = z.infer<typeof NodeExecutionSchema>;

// Execution data (one per chat message in agentic workflows)
export const ExecutionSchema = z.object({
  id: z.string(),
  runId: z.string(),
  status: ExecutionStatusSchema,
  timestamp: z.string(),
  duration: z.number().optional(),
  inputMessage: z.string().optional(),
  outputMessage: z.string().optional(),
  nodeExecutions: z.array(NodeExecutionSchema),
});
export type Execution = z.infer<typeof ExecutionSchema>;

// Workflow definition schema (for run snapshots)
export const WorkflowDefinitionSnapshotSchema = z.object({
  definition: z.record(z.unknown()),
  kind: z.string().optional(),
});
export type WorkflowDefinitionSnapshot = z.infer<typeof WorkflowDefinitionSnapshotSchema>;

// Run data
export const RunSchema = z.object({
  id: z.string(),
  workflowId: z.string(),
  workflowName: z.string(),
  status: RunStatusSchema,
  startTime: z.string(),
  endTime: z.string().optional(),
  duration: z.number().optional(),
  isAgentic: z.boolean(),
  executionCount: z.number(),
  executions: z.array(ExecutionSchema).optional(),
  /** Whether this run was triggered from a draft workflow */
  isDraftRun: z.boolean().optional(),
  /** Snapshot of the workflow definition that was executed (from run response) */
  workflowSnapshot: WorkflowDefinitionSnapshotSchema.optional(),
});
export type Run = z.infer<typeof RunSchema>;

// Version filter for draft vs published runs
export const VersionFilterSchema = z.enum(['all', 'draft', 'published']);
export type VersionFilter = z.infer<typeof VersionFilterSchema>;

// Filter state
export const RunFiltersSchema = z.object({
  statuses: z.array(RunStatusSchema),
  workflowName: z.string().optional(),
  dateRange: z.object({
    start: z.string().optional(),
    end: z.string().optional(),
  }).optional(),
  durationRange: z.object({
    min: z.number().optional(),
    max: z.number().optional(),
  }).optional(),
  searchQuery: z.string().optional(),
  /** Filter by workflow version (all, draft, published) */
  versionFilter: VersionFilterSchema.optional(),
});
export type RunFilters = z.infer<typeof RunFiltersSchema>;

// Chat message for live interaction
export const ChatMessageSchema = z.object({
  id: z.string(),
  role: z.enum(['user', 'assistant', 'system']),
  content: z.string(),
  timestamp: z.string(),
  executionId: z.string().optional(),
});
export type ChatMessage = z.infer<typeof ChatMessageSchema>;

// Default empty filters
export const defaultFilters: RunFilters = {
  statuses: [],
  workflowName: undefined,
  dateRange: undefined,
  durationRange: undefined,
  searchQuery: undefined,
};

// ---------------------------------------------------------------------------
// Trigger history
// ---------------------------------------------------------------------------
//
// A trigger history row is recorded by the runtime every time a trigger fires
// OR is evaluated and intentionally not fired (e.g. a Recurrence trigger that
// finds no new items). It is the audit log for trigger evaluation, separate
// from the run history. The runtime returns TitleCase status strings; we
// normalize to lowercase at the API boundary so the same RunStatusBadge can
// render both runs and trigger histories.

export const TriggerHistoryStatusSchema = z.enum([
  'running',
  'succeeded',
  'failed',
  'cancelled',
  'skipped',
]);
export type TriggerHistoryStatus = z.infer<typeof TriggerHistoryStatusSchema>;

// Wider status union used by RunStatusBadge so it can render both Run and
// TriggerHistory rows without two parallel badge components.
export const MonitoringStatusSchema = z.enum([
  'running',
  'succeeded',
  'failed',
  'cancelled',
  'skipped',
]);
export type MonitoringStatus = z.infer<typeof MonitoringStatusSchema>;

// Lightweight pointer to the inputs/outputs blob the runtime can stream on
// demand. Mirrors the shape returned by the runtime for run inputs/outputs.
export const TriggerContentLinkSchema = z.object({
  uri: z.string().optional(),
  contentSize: z.number().optional(),
  contentVersion: z.string().optional(),
});
export type TriggerContentLink = z.infer<typeof TriggerContentLinkSchema>;

export const TriggerHistorySchema = z.object({
  /** Fully-qualified resource id from the runtime. */
  id: z.string(),
  /** Short history name; equals the runId when `fired` is true. */
  name: z.string(),
  /** Name of the trigger this history row belongs to (e.g. "manual"). */
  triggerName: z.string(),
  status: TriggerHistoryStatusSchema,
  /** True when the trigger fire produced a run; false when it was skipped. */
  fired: z.boolean(),
  startTime: z.string(),
  endTime: z.string().optional(),
  scheduledTime: z.string().optional(),
  /** HTTP-style status code (e.g. "OK", "BadRequest") when applicable. */
  code: z.string().optional(),
  error: ErrorDetailsSchema.optional(),
  /** Caller-provided correlation id (LA Standard parity). */
  clientTrackingId: z.string().optional(),
  inputsLink: TriggerContentLinkSchema.optional(),
  outputsLink: TriggerContentLinkSchema.optional(),
  /** Run produced by this trigger fire (only present when `fired` is true). */
  run: z.object({ name: z.string(), id: z.string() }).optional(),
});
export type TriggerHistory = z.infer<typeof TriggerHistorySchema>;

export const TriggerHistoryFiltersSchema = z.object({
  statuses: z.array(TriggerHistoryStatusSchema),
  startTime: z.object({
    from: z.string().optional(),
    to: z.string().optional(),
  }).optional(),
  clientTrackingId: z.string().optional(),
});
export type TriggerHistoryFilters = z.infer<typeof TriggerHistoryFiltersSchema>;

// Default: hide noisy "Skipped" rows. Recurrence triggers can produce huge
// volumes of skipped histories; surface them only when the user opts in.
// (Addresses load-balancing concern raised on the issue.)
export const defaultTriggerHistoryFilters: TriggerHistoryFilters = {
  statuses: ['running', 'succeeded', 'failed', 'cancelled'],
};

const DEFAULT_TRIGGER_HISTORY_STATUS_SET = new Set<TriggerHistoryStatus>(
  defaultTriggerHistoryFilters.statuses
);

/**
 * Returns true when the user has changed the status selection away from the
 * built-in default (which is non-empty because it intentionally hides
 * Skipped). A naive `statuses.length > 0` check would mark the initial state
 * as "active" and surface the Clear filters CTA / filter badge on first load.
 */
export const isTriggerHistoryStatusFilterActive = (
  statuses: readonly TriggerHistoryStatus[]
): boolean => {
  if (statuses.length !== DEFAULT_TRIGGER_HISTORY_STATUS_SET.size) return true;
  for (const s of statuses) {
    if (!DEFAULT_TRIGGER_HISTORY_STATUS_SET.has(s)) return true;
  }
  return false;
};

export const MonitoringModeSchema = z.enum(['runs', 'triggers']);
export type MonitoringMode = z.infer<typeof MonitoringModeSchema>;
